/***
 *
 *   MODAL
 *   Display an overlay modal anywhere in your application by calling
 *   context.modal.show() with an object containing the following props
 *
 *   PROPS
 *   title: (optional)
 *   text: message to the user (optional)
 *   form: a form object (optional: see form docs for more information)
 *   url: destination to send the form
 *   method: HTTP post type
 *   buttonText – text for the confirm button
 *
 **********/

import { type ComponentProps, type ReactNode, useContext, useRef } from 'react';
import { ViewContext, Card, Form } from 'components/lib';
import { CSSTransition } from 'react-transition-group';
import './modal.scss';

type FormComponentProps = ComponentProps<typeof Form>;

interface ModalProps {
  title?: ReactNode;
  text?: ReactNode;
  customHtml?: ReactNode;
  maxWidth?: number | string;
  form?: FormComponentProps['data'];
  method?: FormComponentProps['method'];
  url?: FormComponentProps['url'];
  customSubmit?: FormComponentProps['customSubmit'];
  destructive?: boolean;
  buttonText?: FormComponentProps['buttonText'];
  onChange?: FormComponentProps['onChange'];
  toastSuccess?: FormComponentProps['toastSuccess'];
  disabled?: boolean;
  preSubmitText?: FormComponentProps['preSubmitText'];
  preSubmitClassName?: FormComponentProps['preSubmitClassName'];
  preSubmitStyle?: FormComponentProps['preSubmitStyle'];
}

export function Modal(props: ModalProps) {
  const context = useContext(ViewContext);
  // React 19 removed ReactDOM.findDOMNode, which CSSTransition uses by default;
  // a nodeRef makes it target the child directly instead.
  const nodeRef = useRef(null);

  return (
    <CSSTransition in appear nodeRef={nodeRef} timeout={0} classNames="modal">
      <div ref={nodeRef} className="modal" onClick={(e) => e.target === e.currentTarget && context.modal.hide(true)}>
        <div
          className="modal-content"
          // it was added by Huseyin
          style={{ maxWidth: props.maxWidth, overflow: 'auto' }}
        >
          <Card title={props.title}>
            {props.text && <p>{props.text}</p>}

            {/* customHtml is added by Huseyin */}
            {props.customHtml && <>{props.customHtml}</>}

            {props.form && (
              <Form
                method={props.method}
                url={props.url}
                customSubmit={props.customSubmit}
                data={props.form}
                destructive={props.destructive}
                buttonText={props.buttonText}
                cancel={() => context.modal.hide(true)}
                onChange={props.onChange}
                toastSuccess={props.toastSuccess}
                disabled={props.disabled}
                preSubmitText={props.preSubmitText}
                preSubmitClassName={props.preSubmitClassName}
                preSubmitStyle={props.preSubmitStyle}
              />
            )}
          </Card>
        </div>
      </div>
    </CSSTransition>
  );
}
